import { StateCreator } from 'zustand'
import { mercuryApi } from '$api'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import {
  PayrollEmployee,
  StaffMemberRemunerationSnapshot,
  StaffMember,
  newStaffMember,
  transformRemunerationSnapshotOnSave,
  IUnavailabilityRecord,
  ILeaveType,
  ILeaveRequest,
} from '$models'
import { SharedSlice } from '../../../../../slice'
import {
  Country,
  LoadedUnavailabilityStatus,
  PayrollSystem,
  payrollSystemMapping,
} from '$constants'
import {
  leaveRequestService,
  leaveTypeService,
  payrollService,
  staffMemberService,
  unavailabilityService,
} from '$services'

interface EditStaffMemberState {
  staffMember: StaffMember
  payrollEmployee: PayrollEmployee | null
  payrollEmployees: PayrollEmployee[]
  isLoadingPayrollEmployees: boolean
  unavailabilities: IUnavailabilityRecord[]
  isLoadingUnavailabilities: boolean
  leaveTypes: ILeaveType[]
  leaveRequests: ILeaveRequest[]
  isLoadingLeave: boolean
}

export interface EditStaffMemberSlice extends EditStaffMemberState {
  /**
   * Reset StaffMember Slice
   */
  resetEditStaffMemberSlice: () => void

  setPayrollEmployee: (payrollEmployee: PayrollEmployee | null) => void

  /**
   * Get Staff Member By id
   * @param staffMemberId StaffMember id
   */
  getStaffMember: (staffMemberId: string) => Promise<StaffMember | null>

  /**
   * Get Staff Member personal details
   * @param staffMemberId employee id (staffMember Id)
   * @param country company country received in the query params
   * @param payrollSystem company payroll system received in the query params
   */
  getStaffMemberPersonalDetails: (
    staffMemberId: string,
    country?: Country,
    payrollSystem?: PayrollSystem
  ) => Promise<StaffMember | null>

  /**
   * Get Payroll Employee By id
   * @param payrollEmployeeId Payroll Employee id
   */
  getPayrollEmployee: (
    payrollEmployeeId: string,
    payrollSystem: PayrollSystem
  ) => Promise<PayrollEmployee | null>

  getPayrollEmployees: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Update StaffMember
   * @param staffMember StaffMember to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @param notLoading Do not set the loading state within this method
   * @returns
   */
  updateStaffMember: (
    staffMember: StaffMember,
    successMessage?: string,
    errorMessage?: string,
    notLoading?: boolean
  ) => Promise<StaffMember | null>

  /**
   * Update StaffMember Personal Details
   * @param staffMember StaffMember to update
   * @param country company country received in the query params
   * @param payrollSystem company payroll system received in the query params
   * @param errorMessage Error Message to display if the update has failed
   * @param notLoading Do not set the loading state within this method
   * @returns
   */
  updateStaffMemberPersonalDetails: (
    staffMember: StaffMember,
    country: Country,
    payrollSystem: PayrollSystem,
    errorMessage?: string,
    notLoading?: boolean
  ) => Promise<StaffMember | null>

  /**
   * Update PayrollEmployee
   * @param payrollEmployee PayrollEmployee to update
   * @param payrollSystem The PayrollSystem to send the updated PayrollEmployee to
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  updatePayrollEmployee: (
    payrollEmployee: PayrollEmployee,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Creates a employee in a third party connection
   * @param payrollEmployee the third party employee to create
   * @param payrollSystem The PayrollSystem to create the new employee in
   * @param successMessage Success Message to display if the creation was successful
   * @param errorMessage Error Message to display if the creation has failed
   */
  createPayrollEmployee: (
    payrollEmployee: PayrollEmployee,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<PayrollEmployee | null>

  /**
   * Add a new remuneration snapshot to the current staff member
   * @param remunerationSnapshot The remuneration snapshot to add
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  addRemunerationSnapshot: (
    remunerationSnapshot: StaffMemberRemunerationSnapshot,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Update a remuneration snapshot to the current staff member
   * @param remunerationSnapshot The remuneration snapshot to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  updateRemunerationSnapshot: (
    remunerationSnapshot: StaffMemberRemunerationSnapshot,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Resend StaffMember Onboarding Request By id
   * @param staffMemberId Staff member id
   * @param successMessage Success Message to display if the request was successful
   * @returns Promise<boolean>
   */
  resendStaffMemberOnboaringRequest: (
    staffMemberId: string,
    successMessage?: string
  ) => Promise<boolean>

  /**
   * Get Unavailabilities records for a staff member
   * @param staffMemberId StaffMember id to get the unavailabilities from
   * @returns Promise<Unavailability[]>
   */
  getUnavailabilitiesByStaffMemberId: (staffMemberId: string) => Promise<void>

  /**
   * Add a new unavailability to the current staff member
   * @param unavailability The unavailability to add
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  addUnavailability: (
    unavailability: IUnavailabilityRecord,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Update the status of an existing unavailability record
   * @param id Id of the unavailability
   * @param status The status to add to the unavailability
   * @param successMessage Success message to display if the update was successful
   * @param errorMessage Error Message to display if the update fails
   * @returns
   */
  updateUnavailabilityStatus: (
    id: string,
    status: LoadedUnavailabilityStatus,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Send an unavailability request using the Id of the request
   * @param unavailabilityRecordId Id of the record to send
   * @param successMessage Success message to display if the request was successful
   * @param errorMessage Error message to display if the request fails
   * @returns
   */
  sendUnavailabilityRequestById: (
    unavailabilityRecordId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Get a list of Leave Types filtered by staff member
   * @param staffMemberId Id of the staff member
   * @returns
   */
  getLeaveTypesByStaffMemberId: (staffMemberId: string) => Promise<void>

  /**
   * Get a list of Leave Requests filtered by staff member
   * @param staffMemberId Id of the staff member
   * @returns
   */
  getLeaveRequestsByStaffMemberId: (staffMemberId: string) => Promise<void>

  /**
   * Add a new leave request for a staff member
   * @param leaveRequest The leave request to add
   * @param successMessage Success message if the request completes
   * @param errorMessage Error message if the request fails
   * @returns
   */
  addLeaveRequest: (
    leaveRequest: ILeaveRequest,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: EditStaffMemberState = {
  staffMember: newStaffMember,
  payrollEmployee: null,
  payrollEmployees: [],
  isLoadingPayrollEmployees: false,
  unavailabilities: [],
  isLoadingUnavailabilities: false,
  leaveTypes: [],
  leaveRequests: [],
  isLoadingLeave: false,
}

const createEditStaffMemberSlice: StateCreator<
  EditStaffMemberSlice & SharedSlice,
  [],
  [],
  EditStaffMemberSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditStaffMemberSlice: () => set(initialState),

  setPayrollEmployee: (payrollEmployee: PayrollEmployee | null) => {
    set(() => ({ payrollEmployee: payrollEmployee }))
  },

  // State API calls
  getStaffMember: async (
    staffMemberId: string
  ): Promise<StaffMember | null> => {
    return staffMemberService
      .getById(staffMemberId)
      .then((result) => {
        set(() => ({ staffMember: result }))

        return result
      })
      .catch((error) => {
        set(() => ({
          staffMember: newStaffMember,
        }))

        exceptionMessageHandler(error, 'Failed to retrieve staff member')
        return null
      })
  },

  getStaffMemberPersonalDetails: async (
    staffMemberId: string,
    country?: Country,
    payrollSystem?: PayrollSystem
  ): Promise<StaffMember | null> => {
    return staffMemberService
      .getPersonalDetailsById(staffMemberId, country, payrollSystem)
      .then((result) => {
        set(() => ({ staffMember: result }))

        return result
      })
      .catch((error) => {
        set(() => ({
          staffMember: newStaffMember,
        }))

        exceptionMessageHandler(error, 'Failed to retrieve staff member')
        return null
      })
  },

  getPayrollEmployee: async (
    payrollEmployeeId: string,
    payrollSystem: PayrollSystem
  ): Promise<PayrollEmployee | null> => {
    const payrollSystemMap = payrollSystemMapping[payrollSystem]

    return payrollService
      .getPayrollEmployee(payrollSystem, payrollEmployeeId)
      .then((result) => {
        const payrollEmployee = payrollSystemMap.transformOnGet
          ? payrollSystemMap.transformOnGet(result)
          : result
        set(() => ({
          payrollEmployee: payrollEmployee,
        }))
        return payrollEmployee
      })
      .catch((error) => {
        logger.error(
          `Failed to retrieve payroll employee ${payrollEmployeeId}`,
          error
        )
        set(() => ({
          payrollEmployee: null,
        }))

        exceptionMessageHandler(error, 'Failed to retrieve payroll employee')
        return null
      })
  },

  getPayrollEmployees: async (payrollSystem: PayrollSystem): Promise<void> => {
    try {
      set(() => ({ isLoadingPayrollEmployees: true }))

      const payrollEmployees: PayrollEmployee[] =
        await payrollService.getPayrollEmployees(payrollSystem)

      if (!payrollEmployees) {
        logger.error('Payroll employees response returned null')
        set(() => ({
          payrollEmployees: [],
        }))
        enqueueLoadedSnackbar('Failed to retrieve payroll employees', 'error')
      }

      set(() => ({ payrollEmployees: payrollEmployees }))
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve payroll employees')
    } finally {
      set(() => ({ isLoadingPayrollEmployees: false }))
    }
  },

  updateStaffMember: async (
    staffMember: StaffMember,
    successMessage?: string,
    errorMessage?: string,
    notLoading?: boolean
  ): Promise<StaffMember | null> => {
    try {
      !notLoading && set(() => ({ isLoading: true }))
      const updatedStaffMember = await staffMemberService.update(staffMember)

      if (!updatedStaffMember) {
        logger.error(`${errorMessage} - updatedStaffMember doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      set(() => ({
        staffMember: updatedStaffMember,
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
      return updatedStaffMember
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    } finally {
      !notLoading && set(() => ({ isLoading: false }))
    }
  },

  updateStaffMemberPersonalDetails: async (
    staffMember: StaffMember,
    country: Country,
    payrollSystem: PayrollSystem,
    errorMessage?: string,
    notLoading?: boolean
  ): Promise<StaffMember | null> => {
    try {
      !notLoading && set(() => ({ isLoading: true }))
      const updatedStaffMember = await staffMemberService.updatePersonalDetails(
        staffMember,
        country,
        payrollSystem
      )

      if (!updatedStaffMember) {
        logger.error(`${errorMessage} - updatedStaffMember doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return null
      }

      set(() => ({
        staffMember: updatedStaffMember,
      }))
      return updatedStaffMember
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return null
    } finally {
      !notLoading && set(() => ({ isLoading: false }))
    }
  },

  updatePayrollEmployee: async (
    payrollEmployee: PayrollEmployee,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const payrollSystemMap = payrollSystemMapping[payrollSystem]
      const transformedPayrollEmployeeWhenSave =
        payrollSystemMap.transformOnSave
          ? payrollSystemMap.transformOnSave(payrollEmployee)
          : payrollEmployee

      const updatedPayrollEmployee: PayrollEmployee = await mercuryApi.update(
        `/payroll/employees/${payrollEmployee.id}?system=${payrollSystem}`,
        transformedPayrollEmployeeWhenSave
      )
      if (!updatedPayrollEmployee) {
        logger.error(`${errorMessage} - updatedPayrollEmployee doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      const transformedPayrollEmployeeWhenGet = payrollSystemMap.transformOnGet
        ? payrollSystemMap.transformOnGet(payrollEmployee)
        : payrollEmployee

      set(() => ({
        payrollEmployee: transformedPayrollEmployeeWhenGet,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  createPayrollEmployee: async (
    payrollEmployee: PayrollEmployee,
    payrollSystem: PayrollSystem
  ): Promise<PayrollEmployee | null> => {
    const payrollSystemMap = payrollSystemMapping[payrollSystem]
    const transformedPayrollEmployeeWhenSave = payrollSystemMap.transformOnSave
      ? payrollSystemMap.transformOnSave(payrollEmployee)
      : payrollEmployee

    return mercuryApi
      .add(
        `/payroll/employees?system=${payrollSystem}`,
        transformedPayrollEmployeeWhenSave
      )
      .then((result) => {
        const payrollEmployee = payrollSystemMap.transformOnGet
          ? payrollSystemMap.transformOnGet(result)
          : result
        set((state) => ({
          payrollEmployee: payrollEmployee,
          payrollEmployees: [...state.payrollEmployees, payrollEmployee],
        }))
        return payrollEmployee
      })
      .catch((error) => {
        set(() => ({
          payrollEmployee: null,
        }))

        exceptionMessageHandler(error, 'Failed to create new payroll employee')
        return null
      })
  },

  addRemunerationSnapshot: async (
    remunerationSnapshot: StaffMemberRemunerationSnapshot,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const newRemunerationSnapshot =
        await staffMemberService.addRemunerationSnapshot(remunerationSnapshot)

      if (!newRemunerationSnapshot) {
        logger.error(
          `${errorMessage} - added remuneration snapshot returned with no response`
        )
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  updateRemunerationSnapshot: async (
    remunerationSnapshot: StaffMemberRemunerationSnapshot,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))
      const updatedRemunerationSnapshot = await mercuryApi.update(
        `/staff-members/remuneration/${remunerationSnapshot.id}`,
        transformRemunerationSnapshotOnSave(remunerationSnapshot)
      )

      if (!updatedRemunerationSnapshot) {
        logger.error(
          `${errorMessage} - updated remuneration snapshot returned with no response`
        )

        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  resendStaffMemberOnboaringRequest: async (
    staffMemberId: string,
    successMessage?: string
  ): Promise<boolean> => {
    try {
      await staffMemberService.resendStaffMemberOnboaringRequest(staffMemberId)
      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to resend the onboarding request')
      return false
    }
  },

  getUnavailabilitiesByStaffMemberId: async (
    staffMemberId: string
  ): Promise<void> => {
    return unavailabilityService
      .getByStaffMemberId(staffMemberId)
      .then((result) => {
        set(() => ({
          unavailabilities: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          unavailabilities: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve unavailabilities')
      })
  },

  addUnavailability: async (
    unavailability: IUnavailabilityRecord,
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoadingUnavailabilities: true }))
    return unavailabilityService
      .add(unavailability)
      .then((result) => {
        if (!result) {
          logger.error(`${errorMessage} - unavailability result doesn't exist`)
          enqueueLoadedSnackbar(errorMessage, 'error')
          return
        }

        if (!result.staffMemberId) {
          logger.error(
            `${errorMessage} - unavailability record missing staffMemberId`
          )
          enqueueLoadedSnackbar(errorMessage, 'error')
          return
        }

        unavailabilityService
          .getByStaffMemberId(result.staffMemberId)
          .then((result) => {
            set(() => ({
              unavailabilities: result,
            }))
          })

        enqueueLoadedSnackbar(successMessage, 'success')
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
      })
      .finally(() => {
        set(() => ({ isLoadingUnavailabilities: false }))
      })
  },

  updateUnavailabilityStatus: async (
    id: string,
    status: LoadedUnavailabilityStatus,
    successMessage?: string,
    errorMessage?: string
  ) => {
    return unavailabilityService
      .updateStatus(id, status)
      .then((updatedUnavailability) => {
        set((state) => ({
          unavailabilities: state.unavailabilities.map((unavailability) =>
            unavailability.id === updatedUnavailability.id
              ? updatedUnavailability
              : unavailability
          ),
        }))
        enqueueLoadedSnackbar(successMessage, 'success')
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
      })
  },

  sendUnavailabilityRequestById: async (
    unavailabilityRecordId: string,
    successMessage?: string,
    errorMessage?: string
  ): Promise<void> => {
    return unavailabilityService
      .sendRequest(unavailabilityRecordId)
      .then((updatedUnavailability) => {
        set((state) => ({
          unavailabilities: state.unavailabilities.map((unavailability) =>
            unavailability.id === updatedUnavailability.id
              ? updatedUnavailability
              : unavailability
          ),
        }))
        enqueueLoadedSnackbar(successMessage, 'success')
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
      })
  },

  getLeaveTypesByStaffMemberId: async (
    staffMemberId: string
  ): Promise<void> => {
    return leaveTypeService
      .getAll(staffMemberId)
      .then((result) => {
        set(() => ({
          leaveTypes: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          leaveTypes: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve leave types for staff member'
        )
      })
  },

  getLeaveRequestsByStaffMemberId: async (
    staffMemberId: string
  ): Promise<void> => {
    return leaveRequestService
      .getAll(staffMemberId)
      .then((result) => {
        set(() => ({
          leaveRequests: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          leaveRequests: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve leave requests for staff member'
        )
      })
  },

  addLeaveRequest: async (
    leaveRequest: ILeaveRequest,
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoadingLeave: true }))
    return leaveRequestService
      .add(leaveRequest)
      .then((result) => {
        if (!result) {
          logger.error(`${errorMessage} - leave request result doesn't exist`)
          enqueueLoadedSnackbar(errorMessage, 'error')
          return
        }
        set((state) => ({
          leaveRequests: [...state.leaveRequests, result],
        }))

        enqueueLoadedSnackbar(successMessage, 'success')
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
      })
      .finally(() => {
        set(() => ({ isLoadingLeave: false }))
      })
  },
})

export default createEditStaffMemberSlice
